import type { RequestOptions } from '@@/plugin-request/request';
import { request as umiRequest } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';
import {
  readCurrentParkUuid,
  readCurrentTenantUuid,
} from '@/adapters/tenantStorage';
import { getCurrentUser } from '@/utils/currentMember';
import {
  getIdempotencyKey,
  getMutationActionId,
  getMutationRequestFingerprint,
  isMutationRequest,
  type MutationOptions,
} from './idempotency';

type ScopedRequestOptions = RequestOptions & {
  skipParkContext?: boolean;
  mutation?: MutationOptions;
};

const getPageOrigin = () =>
  typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

const inFlightMutations = new Map<string, Promise<unknown>>();

const readSessionScope = () => {
  const user = getCurrentUser();
  return [
    user?.organizationUuid || user?.lastLoginOrganizationUuid || '',
    user?.uuid || user?.id || '',
    user?.memberUuid || '',
  ];
};

const isProjectBffRequest = (url: string, projectApiUrl: string) => {
  try {
    const pageOrigin = getPageOrigin();
    const projectApiUrlObject = new URL(
      projectApiUrl || pageOrigin,
      pageOrigin,
    );
    const targetUrl = new URL(url, projectApiUrlObject);

    return (
      targetUrl.origin === projectApiUrlObject.origin &&
      targetUrl.pathname.startsWith('/bff/')
    );
  } catch {
    return false;
  }
};

export const createRequest = (baseURL?: string, injectParkContext = false) => {
  const scopedBaseURL = baseURL ?? BizAppConfig.appApiUrl ?? '';
  return <T = unknown>(url: string, options?: ScopedRequestOptions) => {
    // 全局请求拦截器消费并移除 mutation；这里保留 disabled 等提示，
    // 避免查询类 POST 在拦截器中重新开启自动幂等。
    const { skipParkContext, ...requestOptions } = options || {};
    const currentTenantUuid = readCurrentTenantUuid();
    const currentParkUuid = readCurrentParkUuid(currentTenantUuid);
    const shouldInjectParkContext =
      injectParkContext &&
      !skipParkContext &&
      Boolean(currentParkUuid) &&
      isProjectBffRequest(url, scopedBaseURL);

    const requestScope = JSON.stringify([
      requestOptions.baseURL ?? scopedBaseURL,
      currentTenantUuid,
      currentParkUuid,
      ...readSessionScope(),
      shouldInjectParkContext,
    ]);
    const mutationKey =
      isMutationRequest(requestOptions) &&
      getIdempotencyKey(url, requestOptions, requestScope);
    const mutationAction = mutationKey
      ? getMutationActionId(url, requestOptions)
      : undefined;
    // 单飞仅合并同作用域、目标和内容；相同显式键的冲突载荷须交给后端校验。
    const inFlightKey = mutationKey
      ? JSON.stringify([
          mutationAction,
          mutationKey,
          getMutationRequestFingerprint(url, requestOptions, requestScope),
        ])
      : undefined;
    const existing = inFlightKey
      ? inFlightMutations.get(inFlightKey)
      : undefined;
    if (existing) return existing as Promise<T>;

    const promise = (
      umiRequest as unknown as <R = unknown>(
        requestUrl: string,
        requestOptions?: ScopedRequestOptions,
      ) => Promise<R>
    )<T>(url, {
      baseURL: scopedBaseURL,
      ...requestOptions,
      headers: {
        ...(requestOptions.headers || {}),
        ...(mutationKey
          ? {
              'Idempotency-Key': mutationKey,
              'X-Submission-Action': mutationAction,
            }
          : {}),
        ...(shouldInjectParkContext && currentParkUuid
          ? { 'x-park-uuid': currentParkUuid }
          : {}),
      },
    });
    if (!inFlightKey) return promise;
    inFlightMutations.set(inFlightKey, promise);
    const clearInFlight = () => {
      if (inFlightMutations.get(inFlightKey) === promise) {
        inFlightMutations.delete(inFlightKey);
      }
    };
    void promise.then(clearInFlight, clearInFlight);
    return promise;
  };
};

export const createAppRequest = () =>
  createRequest(BizAppConfig.appApiUrl, true);
export const createTenantManagementRequest = () =>
  createRequest(BizAppConfig.appTenantManagementUrl);
